import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CommonService, CommonHttpService } from '../../services';
import { Editor } from 'primeng/editor';
import { CommonMethods, Severity } from '../../helpers/common-methods';
import { ConfirmationService } from 'primeng/api';
import { SpinnerService } from '../../services/spinner.service';

// Interfaces for better type safety
interface UserOption {
  name: string;
  code: string;
  email?: string;
}

interface EmailReportData {
  reportId?: number;
  reportName?: string;
  reportDesc?: string;
  toEmails?: string[];
  subject?: string;
  mailBody?: string;
  reportConfig?: any;
}

interface ApiResponse {
  status: number;
  data?: any;
  message?: string;
}

@Component({
  selector: 'bi-email-report',
  standalone: false,
  templateUrl: './email-report.component.html',
  styleUrl: './email-report.component.scss',
  providers: [ConfirmationService]
})
export class EmailReportComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('editor') editor!: Editor;

  // Component state
  visible: boolean = false;
  emailForm!: FormGroup;

  // Loading states
  isComponentLoading: boolean = false;
  isSubmitting: boolean = false;

  // Data arrays
  userOptions: UserOption[] = [];
  selectedUsers: UserOption[] = [];

  // Report data
  reportData: EmailReportData = {};

  emailBody: string = "Hello <b>{{User}}</b>,<br><br> We are pleased to inform you that the <b>{{reportName}}</b> Report you requested is now available.If you need any further assistance or have questions, don't hesitate to reach out";

  private _subscription: Subscription = new Subscription();

  constructor(
    private _commonService: CommonService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonHttpService: CommonHttpService,
    private _fb: FormBuilder,
    private _commonMethods: CommonMethods,
    private confirmationService: ConfirmationService,
    private spinnerService: SpinnerService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.setupEmailReportSubscription();
    this.loadUserOptions();
  }

  ngAfterViewInit(): void {
    // Ensure editor is properly initialized
    if (this.editor) {
      this.editor.writeValue(this.getDefaultEmailBody());
    }
  }

  initForm(): void {
    this.emailForm = this._fb.group({
      toEmails: [[], [Validators.required]],
      subject: ['', [Validators.required, Validators.maxLength(200)]],
      mailBody: ['', [Validators.required]]
    });
  }

  setupEmailReportSubscription(): void {
    this._subscription.add(
      this._commonService.emailReportState.subscribe((state: any) => {
        if (state.show) {
          this.visible = true;
          this.reportData = this._commonService.emailReportData || {};
          this.patchFormData();
          // Ensure change detector is available before calling detectChanges
          if (this._changeDetectorRef) {
            this._changeDetectorRef.detectChanges();
          }
        } else {
          this.visible = false;
          this.resetForm();
        }
      })
    );
  }

  patchFormData(): void {
    if (this.reportData && this.emailForm) {
      const defaultEmailBody = this.getDefaultEmailBody();
      const processedEmailBody = this.reportData.mailBody ?
        this.processEmailBodyTemplate(this.reportData.mailBody) :
        defaultEmailBody;

      this.emailForm.patchValue({
        toEmails: this.reportData.toEmails || [],
        subject: this.getDefaultEmailSubject(),
        mailBody: processedEmailBody
      });

      // Force update the editor content if it exists
      setTimeout(() => {
        if (this.editor) {
          this.editor.writeValue(this.emailForm.get('mailBody')?.value || processedEmailBody);
        }
      }, 100);
    }
  }

  getDefaultEmailBody(): string {
    const reportName = this.reportData?.reportConfig?.reportName || this.reportData?.reportName || 'Report';
    return `
      <p>Hello,</p>
      <p>We are pleased to inform you that the <strong>${reportName}</strong> Report you requested is now available. If you need any further assistance or have questions, don't hesitate to reach out.</p>
    `;
  }

  getDefaultEmailSubject(): string {
    const reportName = this.reportData?.reportName || this.reportData?.reportConfig?.reportName || 'Report';
    return `Requested Report: ${reportName} is Now Ready`;
  }

  processEmailBodyTemplate(template: string): string {
    const reportName = this.reportData?.reportConfig?.reportName || this.reportData?.reportName || 'Report';
    const reportDesc = this.reportData?.reportConfig?.reportDesc || this.reportData?.reportDesc || 'This report contains the requested data.';

    return template
      .replace(/\{\{User\}\}/g, 'User')
      .replace(/\{\{reportName\}\}/g, reportName)
      .replace(/\{\{reportDesc\}\}/g, reportDesc);
  }

  async loadUserOptions(): Promise<void> {
    // Initialize as empty array to prevent filter errors
    this.userOptions = [];

    this._commonHttpService.getUsersWithEmail().subscribe({
      next: (response: any) => {
        // Ensure response is an array before assigning
        if (Array.isArray(response)) {
          this.userOptions = response;
        } else if (response && Array.isArray(response.data)) {
          this.userOptions = response.data;
        } else if (response && Array.isArray(response.users)) {
          this.userOptions = response.users;
        } else {
          console.warn('Unexpected response format for users:', response);
          this.userOptions = [];
        }

      },
      error: (error) => {
        console.error('Error loading user options:', error);
        this.userOptions = [];
        this._commonMethods.showToastMessage('Error loading users', 'Error', Severity.error);
      }
    });
  }

  onSubmit(): void {
    if (this.emailForm.valid) {
      this.isSubmitting = true;
      this.spinnerService.show();

      const formData = this.emailForm.value;
      // return
      const reportFormData = this._commonService.emailReportData || {};

      // Add email fields to the report form data
      const emailReportData = {
        ...reportFormData,
        toEmails: formData.toEmails,
        subject: formData.subject,
        mailBody: formData.mailBody
      };

      this._commonHttpService.getReportData(emailReportData).subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          this.spinnerService.hide();

          if (response.success === 1) {
            this._commonMethods.showToastMessage(
              response.message || 'Email sent successfully',
              'Success',
              Severity.success
            );
            this.onCancel();
          } else {
            this._commonMethods.showToastMessage(
              response.message || 'Failed to send email',
              'Error',
              Severity.error
            );
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          this.spinnerService.hide();
          console.error('Error sending email:', error);
          this._commonMethods.showToastMessage(
            'Failed to send email. Please try again.',
            'Error',
            Severity.error
          );
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  markFormGroupTouched(): void {
    Object.keys(this.emailForm.controls).forEach(key => {
      const control = this.emailForm.get(key);
      control?.markAsTouched();
    });
  }

  onCancel(): void {
    if (this.emailForm) {
      this.emailForm.reset();
    }
    this._commonService.hideEmailReport();
  }

  resetForm(): void {
    if (this.emailForm) {
      this.emailForm.reset();
    }
    this.selectedUsers = [];
  }

  getEditorInstance(): Editor | null {
    return this.editor;
  }

  setEditorContent(content: string): void {
    if (this.editor) {
      this.editor.writeValue(content);
    }
  }

  getEditorContent(): string {
    if (this.editor) {
      return this.editor.value || '';
    }
    return '';
  }

  isUserSelected(userCode: string): boolean {
    const selectedEmails = this.emailForm.get('toEmails')?.value || [];
    return selectedEmails.includes(userCode);
  }

  removeUser(user: UserOption, event: Event): void {
    event.stopPropagation();
    const selectedEmails = this.emailForm.get('toEmails')?.value || [];
    const updatedEmails = selectedEmails.filter((email: string) => email !== user.code);
    this.emailForm.patchValue({ toEmails: updatedEmails });
  }

  ngOnDestroy(): void {
    this._commonService.hideEmailReport();
    this._subscription.unsubscribe();
  }
} 